import { useEffect, useRef, useState } from "react";

// Mesma lógica usada em sge_infantil_web (src/components/OfflineBanner e
// src/pages/user/login): só se assume "offline" depois de `OFFLINE_DELAY` ms
// sem ligação, para não reagir a falhas momentâneas de rede, e o estado é
// confirmado por polling porque `navigator.onLine`/os eventos 'online' e
// 'offline' só reflectem a interface de rede do dispositivo, não o acesso
// real à internet.
const OFFLINE_DELAY = 10000; // 10 segundos
const POLL_INTERVAL = 3000; // 3 segundos

/**
 * Devolve `true`/`false` conforme o browser tem ou não ligação à internet.
 * Usado para mostrar o aviso de "sem ligação" e para bloquear botões de
 * acção enquanto não houver rede.
 */
const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const offlineTimestamp = useRef(null);

  useEffect(() => {
    const checkNetwork = () => {
      if (!navigator.onLine) {
        if (!offlineTimestamp.current) {
          offlineTimestamp.current = Date.now();
        }
        if (Date.now() - offlineTimestamp.current >= OFFLINE_DELAY) {
          setIsOnline(false);
        }
      } else {
        offlineTimestamp.current = null;
        setIsOnline(true);
      }
    };

    const handleOnline = () => {
      offlineTimestamp.current = null;
      setIsOnline(true);
    };
    const handleOffline = () => {
      if (!offlineTimestamp.current) {
        offlineTimestamp.current = Date.now();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const interval = setInterval(checkNetwork, POLL_INTERVAL);
    checkNetwork();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  return isOnline;
};

export default useOnlineStatus;
